import React, { useCallback, useEffect, useState } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'

import { useApp } from '../../components/AppContext/AppProvider'
import { extractMessageFromServiceError } from '../../services/error.service'

import { ConnectedDotsIcon } from '../Icons/ConnectedDotsIcon'
import { TransitionButton } from '../Buttons/TransitionButton'

import {
  handlerFormState,
  FormState,
  ALMOST_THERE
} from '../ApplicationProgress/types'

import { FormContainerProps } from './types'
import { mapSubmissonPayload } from '../../services/creditApplication.service'
import {
  sendTrackingEvent,
  toButtonEvent
} from '../../services/amplitude.service'
import { AlmostNextButton } from '../ApplicationProgress/AlmostThere/types'

const FormContainer: React.FC<FormContainerProps> = ({
  FormComponent,
  title,
  description,
  handleClick,
  stepState,
  applicationProgress,
  setSubmittedAppication
}) => {
  const { application } = useApp()

  const theme = useTheme()
  const [formValues, setFormValues] = useState<FormState<handlerFormState>>(
    stepState.formState
  )
  const [isComplete, setIsComplete] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const isSubmitStep = stepState.step === ALMOST_THERE.step

  /**
   * hack to remove ts error
   */
  const formData = stepState.formState as any

  useEffect(() => {
    const requiredFields = stepState.requiredFields
    const isRequiredComplete = requiredFields
      ? requiredFields.every((field: keyof handlerFormState) => {
          if (`${field}`.split('.').length > 1) {
            const [prop, subfield] = `${field}`.split('.')
            return formValues[prop as keyof handlerFormState][subfield] ===
              'string'
              ? `${formValues[prop as keyof handlerFormState][subfield]}`.trim()
                  .length > 0
              : !!formValues[prop as keyof handlerFormState][subfield]
          }
          return formValues[field] === 'string'
            ? `${formValues[field]}`.trim().length > 0
            : !!formValues[field]
        })
      : true

    setIsComplete(!loading && isRequiredComplete)
  }, [formValues])

  const setFormData = useCallback(
    (data: handlerFormState) => {
      setFormValues(data)
    },
    [formValues]
  )

  const setFormError = (error: string) => {
    setError(error)
  }

  const navigateStep = (step?: number) => {
    setError(null)

    const nextStep = step || stepState.step + 1

    const currentStep = {
      ...stepState,
      formState: { ...formValues }
    }

    handleClick(nextStep, currentStep)
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    setError(null)
    setLoading(true)

    const submitApplication = stepState.onSubmitApplication
      ? stepState.onSubmitApplication(application)
      : null

    const onSubmitRequest = stepState.onSubmitRequest
      ? stepState.onSubmitRequest(application)
      : () => Promise.resolve()

    Promise.resolve(formValues)
      .then(onSubmitRequest)
      .then(() => {
        if (!isSubmitStep) navigateStep()
        else if (isSubmitStep && submitApplication) {
          setLoading(true)
          sendTrackingEvent(toButtonEvent({ ...AlmostNextButton }))
          return submitApplication(mapSubmissonPayload(applicationProgress))
            .then(setSubmittedAppication)
            .catch((e) => setError(extractMessageFromServiceError(e)))
            .finally(() => setLoading(false)) // add Error catch logic
        }
      })
      .catch((err) => {
        setError(extractMessageFromServiceError(err))
      })
      .finally(() => setLoading(false))
  }

  return (
    <Box sx={{ mt: 2, pr: 2, pl: 2 }}>
      <Typography
        component="h1"
        variant="h5"
        align="center"
        color="text.primary"
        gutterBottom
      >
        {title}
      </Typography>

      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <ConnectedDotsIcon sx={{ height: '1em', width: '2em' }} />
      </div>

      {description && (
        <Typography component="div" variant="body1" align="center" gutterBottom>
          {description}
        </Typography>
      )}

      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit}
        sx={{
          marginTop: theme.spacing(2),
          p: theme.breakpoints.only('xs') ? 0 : 2
        }}
      >
        <FormComponent
          setFormData={setFormData}
          setFormError={setFormError}
          formData={formData}
          error={error}
          navigateStep={navigateStep}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center'
          }}
        >
          <TransitionButton
            text={isSubmitStep ? 'Apply' : 'Next'}
            type="submit"
            loading={loading}
            style={{ width: '8rem' }}
            disabled={loading || !isComplete}
          />
        </Box>
      </Stack>
    </Box>
  )
}

export default FormContainer
